import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';
import { loadHdChartGlobalSettings } from '../../../chart/bodygraph/chart-tabs/hd-chart-global-settings';
import { HdContentLanguageService } from '../../../chart/bodygraph/hd-content-language.service';
import { HD_PLANET_GRID_DEFAULT_COLOR_CONFIG } from '../../../chart/bodygraph/planet-grid/hd-planet-grid-color-config';
import { hexagramBits } from '../../core/iching-core';
import { IChingIndexEntry, IChingLinePosition, IChingMethodContent, IChingReading } from '../../core/iching.models';
import { HdIChingHexagramFigureComponent } from '../../figure/hd-iching-hexagram-figure.component';
import { HdIChingTrigramFigureComponent } from '../../figure/hd-iching-trigram-figure.component';
import { injectIChingTranslate } from '../iching-consult-translate';
import { IChingRootPair, buildRootPairs, listLines } from './iching-reading-derivations';

function nuclearRootColors(): { readonly upper: string; readonly lower: string } {
  const lps = loadHdChartGlobalSettings().planetGridColorConfig?.lpsColumn ?? HD_PLANET_GRID_DEFAULT_COLOR_CONFIG.lpsColumn;
  return { upper: lps.presence, lower: lps.prana };
}

/**
 * De wortel (C3): eerst het bovenste kerntrigram (lijnen 3-4-5), dan het
 * onderste (2-3-4), zoals ze in het kernhexagram op elkaar liggen. Per
 * kerntrigram een afleidingsrij: je hexagram neutraal met de bijdragende
 * lijnen in de kerntrigramkleur, het kerntrigram dat eruit volgt in dezelfde
 * kleur en maat, de duiding en het omhullende trigram (lichtere tint van die
 * kleur). Daarna vormen beide kerntrigrammen samen het kernhexagram, dat je in
 * het paneel kunt openen. De kerntrigrammen hebben eigen kleuren
 * (`--hd-iching-nuclear-upper`/`-lower`), los van amber en `--primary` van
 * de gewone trigrammen in C1: de twee blauwen van het Lunar Planetary Square
 * uit het gekozen planetengrid-palet (presence donker boven, prana lichter
 * onder). De figuren lezen ze via hun CSS-variabelen.
 */
@Component({
  selector: 'hd-iching-root',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [HdIChingHexagramFigureComponent, HdIChingTrigramFigureComponent],
  host: {
    '[style.--hd-iching-nuclear-upper]': 'nuclearColors.upper',
    '[style.--hd-iching-nuclear-lower]': 'nuclearColors.lower',
  },
  styles: `
    :host {
      display: flex; flex-direction: column; gap: 12px; font-size: 13.5px; line-height: 1.5;
    }
    .derivation {
      display: flex; flex-wrap: wrap; align-items: flex-start; gap: 16px 32px; padding: 12px 14px;
      border-radius: var(--radius-md, 6px); background: var(--hd-iching-card-bg, rgba(0, 0, 0, 0.035));
    }
    .derivation[data-root='upper'] { --_nuclear: var(--hd-iching-nuclear-upper); }
    .derivation[data-root='lower'] { --_nuclear: var(--hd-iching-nuclear-lower); }
    .derivation[data-root='upper'], .derivation[data-root='lower'] {
      --_enveloping: color-mix(in srgb, var(--_nuclear) 55%, var(--white, #fbf9f4));
      --hd-iching-highlight: var(--_nuclear);
    }
    .derivation[data-root='hexagram'] {
      margin-top: 8px;
      --hd-iching-upper: var(--hd-iching-nuclear-upper);
      --hd-iching-lower: var(--hd-iching-nuclear-lower);
    }
    .enveloping { --hd-iching-highlight: var(--_enveloping); }
    .visual { display: flex; flex-direction: column; gap: 6px; flex: 0 0 auto; min-width: 220px; }
    .figures { display: flex; align-items: center; gap: 10px; }
    .arrow { font-size: 18px; line-height: 1; opacity: 0.6; }
    .caption { font-size: 12px; opacity: 0.75; max-width: 220px; }
    .body { display: flex; flex-direction: column; gap: 6px; flex: 1 1 240px; min-width: 0; }
    .heading { display: flex; align-items: center; gap: 6px; }
    .label { font-size: 12px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.04em; opacity: 0.7; }
    /* De naam met het getekende trigram ernaast, even groot als de afleidingsfiguren en verticaal gecentreerd. */
    .trigram { display: flex; align-items: center; gap: 10px; font-weight: 600; }
    p { margin: 0; }
    .enveloping { margin-top: 6px; padding-top: 10px; border-top: 1px dashed var(--hd-iching-divider, rgba(0, 0, 0, 0.12)); }
    .self { font-size: 12.5px; font-style: italic; opacity: 0.85; }
    /* Vaste labelkolom en geen rijafstand: de lijnafstand tussen beide trigrammen is dan die van een hexagram. */
    .stack { display: grid; grid-template-columns: max-content 64px; align-items: center; column-gap: 8px; }
    .stack-label { display: inline-flex; align-items: center; gap: 6px; font-size: 11.5px; }
    .stack-text { opacity: 0.7; }
    .swatch { flex: 0 0 auto; width: 10px; height: 10px; border-radius: 2px; background: var(--hd-iching-highlight); }
    .swatch--lower { background: var(--hd-iching-lower); }
    .swatch--upper { background: var(--hd-iching-upper); }
    .enveloping-visual { display: flex; flex-direction: column; gap: 6px; margin: 4px 0 10px; }
    .open {
      align-self: flex-start; display: inline-flex; align-items: center; gap: 6px; padding: 4px 12px;
      border: 1px solid var(--hd-iching-muted-border, rgba(0, 0, 0, 0.18)); border-radius: var(--radius-md, 6px);
      background: transparent; color: inherit; font: inherit; font-size: 13px; cursor: pointer;
    }
    .open:hover { background: rgba(0, 0, 0, 0.05); }
    .number { font-weight: 700; }
    @media (max-width: 599.98px) {
      .derivation { flex-direction: column; }
      .visual { min-width: 0; }
      .body { flex-basis: auto; }
      .caption { max-width: none; }
    }
  `,
  template: `
    @for (pair of pairs(); track pair.kind) {
      <div class="derivation" [attr.data-root]="pair.kind">
        <div class="visual">
          <div class="figures">
            <hd-iching-hexagram-figure data-figure="nuclear" [lines]="reading().lines" [highlightLines]="pair.fromLines" dimOthers />
            <span class="arrow" aria-hidden="true">→</span>
            <hd-iching-trigram-figure data-figure="nuclear-result" [trigram]="pair.nuclearId" size="normal" tone="highlight" />
          </div>
          <span class="caption">{{ t('humanDesign.iching.reading.nuclearFrom', { lines: linesText(pair.fromLines) }) }}</span>
        </div>
        <div class="body">
          <div class="heading">
            <span class="swatch" aria-hidden="true"></span>
            <span class="label">
              {{ pair.kind === 'upper' ? t('humanDesign.iching.reading.nuclearUpper') : t('humanDesign.iching.reading.nuclearLower') }}
            </span>
          </div>
          @if (pair.nuclear; as n) {
            <span class="trigram">
              <hd-iching-trigram-figure data-figure="nuclear-name" [trigram]="pair.nuclearId" size="normal" tone="highlight" />
              {{ n.name }}
            </span>
            <p>{{ pair.kind === 'upper' ? n.asNuclearUpper : n.asNuclearLower }}</p>
          }
          @if (pair.enveloping; as e) {
            <div class="enveloping">
              <div class="heading">
                <span class="swatch" aria-hidden="true"></span>
                <span class="label">{{ t('humanDesign.iching.reading.enveloping') }}</span>
              </div>
              <div class="enveloping-visual">
                <div class="figures">
                  <hd-iching-hexagram-figure
                    data-figure="enveloping"
                    [lines]="reading().lines"
                    [highlightLines]="pair.envelopingLines"
                    dimOthers
                  />
                  <span class="arrow" aria-hidden="true">→</span>
                  <hd-iching-trigram-figure data-figure="enveloping-result" [trigram]="pair.envelopingId" size="normal" tone="highlight" />
                </div>
                <span class="caption">{{ t('humanDesign.iching.reading.envelopingFrom', { lines: linesText(pair.envelopingLines) }) }}</span>
              </div>
              <span class="trigram">
                <hd-iching-trigram-figure data-figure="enveloping-name" [trigram]="pair.envelopingId" size="normal" tone="highlight" />
                {{ e.name }}
              </span>
              <p>{{ e.asEnveloping }}</p>
              @if (pair.envelopedBySelf) {
                <p class="self">{{ t('humanDesign.iching.reading.envelopedBySelf') }}</p>
              }
            </div>
          }
        </div>
      </div>
    }

    <div class="derivation" data-root="hexagram">
      <div class="visual">
        <div class="figures">
          <div class="stack">
            <span class="stack-label" data-stack-label="upper">
              <span class="swatch swatch--upper" aria-hidden="true"></span>
              <span class="stack-text">{{ t('humanDesign.iching.reading.nuclearAbove') }}</span>
            </span>
            <hd-iching-trigram-figure data-stack="upper" [trigram]="reading().nuclearUpper" size="normal" tone="upper" />
            <span class="stack-label" data-stack-label="lower">
              <span class="swatch swatch--lower" aria-hidden="true"></span>
              <span class="stack-text">{{ t('humanDesign.iching.reading.nuclearBelow') }}</span>
            </span>
            <hd-iching-trigram-figure data-stack="lower" [trigram]="reading().nuclearLower" size="normal" tone="lower" />
          </div>
          <span class="arrow" aria-hidden="true">→</span>
          <hd-iching-hexagram-figure [figure]="nuclearFigure()" size="large" colorTrigrams />
        </div>
        <span class="caption">{{ t('humanDesign.iching.reading.nuclearHexagramFrom') }}</span>
      </div>
      <div class="body">
        <button type="button" class="open" data-test-id="iching-open-nuclear" (click)="openHexagram.emit(reading().nuclearHexagram)">
          {{ t('humanDesign.iching.reading.nuclearHexagram') }}
          <span class="number">#{{ reading().nuclearHexagram }}</span>
          @if (nuclearEntry(); as entry) {
            <span>{{ entry.title }}</span>
          }
        </button>
      </div>
    </div>
  `,
})
export class HdIChingRootComponent {
  readonly reading = input.required<IChingReading>();
  readonly method = input.required<IChingMethodContent>();
  readonly nuclearEntry = input<IChingIndexEntry | undefined>(undefined);
  readonly openHexagram = output<number>();

  protected readonly t = injectIChingTranslate();
  /** Blauwen van het Lunar Planetary Square (presence en prana) uit het opgeslagen planetengrid-palet. */
  protected readonly nuclearColors = nuclearRootColors();
  private readonly contentLang = inject(HdContentLanguageService);

  protected readonly nuclearFigure = computed(() => hexagramBits(this.reading().nuclearHexagram));

  protected readonly pairs = computed<IChingRootPair[]>(() => buildRootPairs(this.reading(), this.method()));

  /** "2, 3 en 4" in de content-taal. */
  protected linesText(lines: readonly IChingLinePosition[]): string {
    return listLines(lines, this.contentLang.effectiveLang());
  }
}
